import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % items.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;
  const active = items[index];

  const go = (next: number) => {
    setIndex((next + items.length) % items.length);
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="今日推荐"
    >
      <div className="absolute inset-0">
        {items.map((item, itemIndex) => (
          <img
            key={item.id}
            src={item.still ?? item.poster}
            alt={`${item.title} 剧照`}
            className={[
              "absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-700",
              itemIndex === index ? "opacity-100" : "opacity-0",
            ].join(" ")}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/80 to-primary-950/20" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/40" />
      </div>

      <div className="relative mx-auto flex w-full max-w-[1400px] flex-col justify-center px-4 pb-10 pt-8 md:px-6 md:pb-16 md:pt-12">
        <div className="min-h-[300px] md:min-h-[380px]">
          <p className="font-label text-[11px] tracking-[0.3em] text-accent-400">FEATURED TODAY · 今日推荐</p>
          <h1 className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xl font-bold text-background-50 md:text-2xl">
            姐姐的朋友
            <span className="text-xs font-normal tracking-wide text-primary-200 md:text-sm">精品影视资源与剧情介绍</span>
          </h1>

          <div key={active.id} className="animate-fade-up">
            <h2 className="mt-6 text-3xl font-black leading-tight text-background-50 md:mt-8 md:text-5xl">
              {active.title}
            </h2>
            <p className="mt-1 font-label text-xs tracking-[0.18em] text-primary-300">{active.originalTitle.toUpperCase()}</p>

            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="flex items-center gap-1 rounded-full bg-accent-500 px-2.5 py-1 font-label font-bold text-primary-950">
                <span className="flex h-3 w-3 items-center justify-center">
                  <i className="ri-star-fill text-[11px]" />
                </span>
                {active.rating.toFixed(1)}
              </span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-1 text-background-100">{active.type}</span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-1 text-background-100">{active.region}</span>
              <span className="text-primary-200">
                {active.year} · {active.genres.join(" / ")} · {active.duration}
              </span>
            </div>

            <p className="mt-4 max-w-xl text-sm leading-7 text-background-100/90 md:text-base md:leading-8">
              {active.summary}
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${active.id}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                查看剧情详情
                <i className="ri-arrow-right-line text-base" />
              </Link>
              <Link
                to={`/category?type=${encodeURIComponent(active.type)}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm text-background-50 transition-colors hover:border-accent-400 hover:text-accent-400"
              >
                <i className="ri-film-line text-base" />
                更多{active.type}
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {items.map((item, itemIndex) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到 ${item.title}`}
                onClick={() => setIndex(itemIndex)}
                className={[
                  "h-1.5 cursor-pointer rounded-full transition-all",
                  itemIndex === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/35 hover:bg-background-50/60",
                ].join(" ")}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <span className="mr-1 font-label text-xs text-primary-300">
              {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </span>
            <button
              type="button"
              aria-label="上一个推荐"
              onClick={() => go(index - 1)}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-base" />
            </button>
            <button
              type="button"
              aria-label="下一个推荐"
              onClick={() => go(index + 1)}
              className="grid h-8 w-8 cursor-pointer place-items-center rounded-md border border-background-50/30 text-background-50 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-base" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}